import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Clock, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

interface TrainingAssignment {
  id: string;
  title: string;
  summary: string;
  whatChanged: string;
  whyItChanged: string;
  whatToDoDifferently: string;
  regulatoryReference: string | null;
  effectiveDate: string | null;
  assignedRole: string;
  assignedAt: string;
  dueDate: string;
  status: 'pending' | 'viewed' | 'acknowledged' | 'overdue';
  acknowledgedAt: string | null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function MyTrainingPage() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['my-training'],
    queryFn: () => api.training.myTraining(),
    staleTime: 30_000,
  });

  const viewMutation = useMutation({
    mutationFn: (id: string) => api.training.markViewed(id),
  });

  const ackMutation = useMutation({
    mutationFn: (id: string) => api.training.acknowledge(id),
    onSuccess: () => {
      toast.success('Training acknowledged — evidence recorded');
      queryClient.invalidateQueries({ queryKey: ['my-training'] });
      setExpanded(null);
    },
    onError: () => toast.error('Failed to acknowledge training'),
  });

  const handleExpand = useCallback((id: string, status: string) => {
    setExpanded((prev) => (prev === id ? null : id));
    if (status === 'pending') viewMutation.mutate(id);
  }, [viewMutation]);

  const handleAcknowledge = useCallback((id: string) => {
    if (window.confirm('By acknowledging, you confirm that you have read and understand the updated procedure. This creates a permanent record.')) {
      ackMutation.mutate(id);
    }
  }, [ackMutation]);

  const pending = (data?.data?.pending ?? []) as unknown as TrainingAssignment[];
  const overdue = (data?.data?.overdue ?? []) as unknown as TrainingAssignment[];
  const completed = (data?.data?.completed ?? []) as unknown as TrainingAssignment[];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-7 w-7 text-teal-400" />
        <div>
          <h1 className="text-2xl font-bold">My Training</h1>
          <p className="text-sm text-muted-foreground">Procedural changes requiring your acknowledgment</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />)}</div>
      ) : (
        <>
          {overdue.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-2 text-compliance-critical flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Overdue ({overdue.length})
              </h2>
              <div className="space-y-2">
                {overdue.map((t) => (
                  <AssignmentCard key={t.id} assignment={t} expanded={expanded === t.id}
                    onExpand={() => handleExpand(t.id, t.status)}
                    onAcknowledge={() => handleAcknowledge(t.id)}
                    isAcking={ackMutation.isPending} urgent />
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Pending ({pending.length})
            </h2>
            {pending.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                No pending training. You're all caught up.
              </div>
            ) : (
              <div className="space-y-2">
                {pending.map((t) => (
                  <AssignmentCard key={t.id} assignment={t} expanded={expanded === t.id}
                    onExpand={() => handleExpand(t.id, t.status)}
                    onAcknowledge={() => handleAcknowledge(t.id)}
                    isAcking={ackMutation.isPending} />
                ))}
              </div>
            )}
          </section>

          {completed.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-2 flex items-center gap-2 text-compliance-low">
                <CheckCircle2 className="h-4 w-4" /> Completed ({completed.length})
              </h2>
              <div className="space-y-2">
                {completed.map((t) => (
                  <div key={t.id} className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{t.title}</p>
                      <p className="text-xs text-muted-foreground">
                        Acknowledged {t.acknowledgedAt ? formatDate(t.acknowledgedAt) : 'recently'}
                      </p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-compliance-low" />
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function AssignmentCard({
  assignment, expanded, onExpand, onAcknowledge, isAcking, urgent,
}: {
  readonly assignment: TrainingAssignment;
  readonly expanded: boolean;
  readonly onExpand: () => void;
  readonly onAcknowledge: () => void;
  readonly isAcking: boolean;
  readonly urgent?: boolean;
}) {
  const days = daysUntil(assignment.dueDate);
  const dueLabel = days < 0 ? `${-days} day${days === -1 ? '' : 's'} overdue` : days === 0 ? 'Due today' : `${days} day${days === 1 ? '' : 's'} left`;

  return (
    <div className={`rounded-lg border bg-card overflow-hidden ${urgent ? 'border-compliance-critical/40' : 'border-border'}`}>
      <button
        type="button"
        onClick={onExpand}
        className="flex w-full items-start gap-3 p-4 text-left hover:bg-muted/30"
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown className="h-4 w-4 shrink-0 mt-0.5" /> : <ChevronRight className="h-4 w-4 shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{assignment.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{assignment.summary}</p>
          <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
            <span>Role: <span className="font-medium">{assignment.assignedRole}</span></span>
            <span className={urgent ? 'text-compliance-critical font-medium' : ''}>{dueLabel}</span>
            {assignment.regulatoryReference && <span>{assignment.regulatoryReference}</span>}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border px-4 py-4 space-y-4 text-sm">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">What Changed</h4>
            <p className="whitespace-pre-wrap">{assignment.whatChanged}</p>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Why It Changed</h4>
            <p className="whitespace-pre-wrap">{assignment.whyItChanged}</p>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">What To Do Differently</h4>
            <p className="whitespace-pre-wrap">{assignment.whatToDoDifferently}</p>
          </div>

          <div className="rounded border border-border bg-muted/30 p-3">
            <p className="text-xs italic text-muted-foreground">
              By clicking "I Understand," you confirm that you have read and understand the changes to "{assignment.title}" and will follow the updated procedures{assignment.effectiveDate ? ` effective ${formatDate(assignment.effectiveDate)}` : ' immediately'}. This creates a permanent, immutable record.
            </p>
          </div>

          <button
            type="button"
            onClick={onAcknowledge}
            disabled={isAcking}
            className="w-full rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
          >
            {isAcking ? 'Recording acknowledgment...' : 'I Understand'}
          </button>
        </div>
      )}
    </div>
  );
}
